/**
 * BL-180 Gate 4 — Xero OAuth 2.0 + PKCE helpers
 *
 * Pure helper functions for the Xero OAuth 2.0 + PKCE flow.  This module
 * contains NO I/O or SDK calls — it builds URLs, generates PKCE values, and
 * parses token responses so that both Edge Functions and the React UI can
 * import it without pulling in server-only dependencies.
 *
 * Credentials (XERO_CLIENT_ID, XERO_CLIENT_SECRET) are read from environment
 * variables at call time so that this module is safe to import in any context.
 * All callers must check that those variables are set and fail gracefully with a
 * clear error message when they are not — see the Edge Functions for the
 * canonical pattern.
 *
 * Xero OAuth 2.0 documentation:
 *   https://developer.xero.com/documentation/guides/oauth2/overview/
 *
 * Xero token lifetimes:
 *   • Access token:  30 minutes
 *   • Refresh token: ~60 days of inactivity (Xero does not return an explicit
 *     expiry in the token response; we track from last use)
 *
 * PKCE:
 *   Xero requires PKCE for all OAuth 2.0 flows.  This module provides helpers
 *   to generate the code_verifier / code_challenge pair and include them in the
 *   authorisation URL and token exchange.
 *
 * Reconnection warning threshold: status is set to 'token_expired' when the
 * refresh token is within REFRESH_TOKEN_WARN_DAYS days of our locally tracked
 * expiry so the user has time to reconnect before being locked out.
 */

// ── Constants ─────────────────────────────────────────────────────────────────

export const XERO_AUTH_BASE_URL = 'https://login.xero.com/identity/connect/authorize';
export const XERO_TOKEN_URL = 'https://identity.xero.com/connect/token';
export const XERO_REVOKE_URL = 'https://identity.xero.com/connect/revocation';
export const XERO_CONNECTIONS_URL = 'https://api.xero.com/connections';

/**
 * Xero scopes required for invoice + payment + contact sync.
 * offline_access is required to receive a refresh token.
 */
export const XERO_SCOPE = 'openid profile email accounting.transactions accounting.contacts offline_access';

/**
 * Days remaining on the (locally-tracked) refresh token expiry below which a
 * reconnection prompt is shown to the user (status → 'token_expired').
 *
 * Xero's refresh token is valid for ~60 days of inactivity; we warn at 10 days
 * to align with the QBO pattern and give users enough runway to reconnect.
 */
export const REFRESH_TOKEN_WARN_DAYS = 10;

/**
 * Xero refresh token inactivity window in days.  We use this to compute the
 * locally-tracked xero_refresh_token_expires_at when Xero does not include one
 * in its token response.
 */
export const XERO_REFRESH_TOKEN_INACTIVITY_DAYS = 60;

// ── PKCE helpers ──────────────────────────────────────────────────────────────

/**
 * Generates a cryptographically random PKCE code_verifier.
 * The verifier is a 96-character URL-safe base64 string (128 bytes of entropy).
 *
 * Works in browser, Deno, and Node.js 18+ (Web Crypto API).
 *
 * @returns {string}  A URL-safe base64-encoded code_verifier.
 */
export function generateCodeVerifier() {
  const array = new Uint8Array(96);
  globalThis.crypto.getRandomValues(array);
  // Convert to URL-safe base64 (no padding, no +, no /).
  return bufferToBase64Url(array.buffer);
}

/**
 * Derives the PKCE code_challenge from a code_verifier using S256 (SHA-256).
 *
 * @param {string} codeVerifier
 * @returns {Promise<string>}  URL-safe base64-encoded SHA-256 of the verifier.
 */
export async function deriveCodeChallenge(codeVerifier) {
  const encoder = new globalThis.TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
  return bufferToBase64Url(hashBuffer);
}

/**
 * Converts an ArrayBuffer to a URL-safe base64 string (no padding).
 *
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  // btoa is available in browsers and Deno; Buffer.from is available in Node.
  let base64;
  if (typeof btoa === 'function') {
    base64 = btoa(binary);
  } else {
    base64 = Buffer.from(binary, 'binary').toString('base64');
  }
  // Convert to URL-safe base64 and strip padding.
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ── URL builder ───────────────────────────────────────────────────────────────

/**
 * Builds the Xero OAuth 2.0 + PKCE authorisation URL that redirects the user
 * to the Xero consent screen.
 *
 * @param {{
 *   clientId: string,
 *   redirectUri: string,
 *   state: string,
 *   codeChallenge: string,
 *   scope?: string,
 * }} params
 * @returns {string}  The full authorisation URL.
 * @throws {Error}    If any required field is missing.
 */
export function buildXeroAuthUrl({ clientId, redirectUri, state, codeChallenge, scope = XERO_SCOPE }) {
  if (!clientId) throw new Error('XERO_CLIENT_ID is not set — cannot build auth URL.');
  if (!redirectUri) throw new Error('redirectUri is required — cannot build auth URL.');
  if (!state) throw new Error('state is required to prevent CSRF — cannot build auth URL.');
  if (!codeChallenge) throw new Error('codeChallenge is required for PKCE — cannot build auth URL.');

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  return `${XERO_AUTH_BASE_URL}?${params.toString()}`;
}

// ── Token exchange ────────────────────────────────────────────────────────────

/**
 * Parses the raw token response from Xero and returns normalised token data.
 *
 * Xero token response shape:
 *   {
 *     access_token: string,
 *     refresh_token: string,
 *     expires_in: number,   // seconds until access token expires (~1800)
 *     token_type: 'Bearer',
 *     scope: string,
 *     // NOTE: Xero does NOT include a refresh token expiry field.
 *     // We compute xero_refresh_token_expires_at from issuedAt +
 *     // XERO_REFRESH_TOKEN_INACTIVITY_DAYS as a locally-tracked proxy.
 *   }
 *
 * @param {object} rawResponse  Parsed JSON body from the Xero token endpoint.
 * @param {Date}   [issuedAt]   Reference time; defaults to `new Date()`.
 * @returns {{
 *   accessToken: string,
 *   refreshToken: string,
 *   tokenExpiresAt: string,           // ISO 8601
 *   refreshTokenExpiresAt: string,    // ISO 8601 — locally tracked
 * }}
 * @throws {Error}  If the response does not contain expected token fields.
 */
export function parseTokenResponse(rawResponse, issuedAt = new Date()) {
  const { access_token, refresh_token, expires_in } = rawResponse ?? {};

  if (!access_token) throw new Error('Xero token response missing access_token.');
  if (!refresh_token) throw new Error('Xero token response missing refresh_token.');
  if (typeof expires_in !== 'number') throw new Error('Xero token response missing expires_in.');

  const tokenExpiresAt = new Date(issuedAt.getTime() + expires_in * 1000).toISOString();
  // Compute locally-tracked refresh token expiry from the inactivity window.
  const refreshTokenExpiresAt = new Date(
    issuedAt.getTime() + XERO_REFRESH_TOKEN_INACTIVITY_DAYS * 24 * 60 * 60 * 1000
  ).toISOString();

  return { accessToken: access_token, refreshToken: refresh_token, tokenExpiresAt, refreshTokenExpiresAt };
}

// ── Reconnection prompt logic ─────────────────────────────────────────────────

/**
 * Returns true when a Xero connection's locally-tracked refresh token expiry
 * is expired or within REFRESH_TOKEN_WARN_DAYS, meaning the user must
 * re-authenticate.
 *
 * @param {{ xero_refresh_token_expires_at: string | null }} connection
 * @param {Date} [now]  Reference time; defaults to `new Date()`.
 * @returns {boolean}
 */
export function isRefreshTokenExpiring(connection, now = new Date()) {
  const expiresAt = connection?.xero_refresh_token_expires_at;
  if (!expiresAt) return false;

  const msRemaining = new Date(expiresAt).getTime() - now.getTime();
  const daysRemaining = msRemaining / (1000 * 60 * 60 * 24);
  return daysRemaining <= REFRESH_TOKEN_WARN_DAYS;
}

/**
 * Returns true when a Xero connection's access token has expired (or will
 * expire within the next 60 seconds — a small buffer to avoid using a token
 * that is about to expire mid-request).
 *
 * @param {{ xero_token_expires_at: string | null }} connection
 * @param {Date} [now]  Reference time; defaults to `new Date()`.
 * @returns {boolean}
 */
export function isAccessTokenExpired(connection, now = new Date()) {
  const expiresAt = connection?.xero_token_expires_at;
  if (!expiresAt) return true; // treat missing as expired

  const bufferMs = 60 * 1000; // 60-second buffer
  return new Date(expiresAt).getTime() - now.getTime() <= bufferMs;
}

// ── Basic Auth header builder ─────────────────────────────────────────────────

/**
 * Builds the Base64-encoded Basic Auth header value required by the Xero token
 * endpoint for the authorization_code and refresh_token grant types
 * (`client_id:client_secret`).
 *
 * Works in both browser (btoa) and Deno/Node (Buffer) environments.
 *
 * @param {string} clientId
 * @param {string} clientSecret
 * @returns {string}  The value to pass as `Authorization: Basic <value>`.
 */
export function buildBasicAuthHeader(clientId, clientSecret) {
  const credentials = `${clientId}:${clientSecret}`;
  if (typeof btoa === 'function') {
    return btoa(credentials);
  }
  return Buffer.from(credentials).toString('base64');
}
